import { GradientNames, type IconNames, Text } from '@cinch-labs/design-system'
import { TrackingEventTypes, useAnalytics } from '@cinch-nx/shared-analytics'
import classnames from 'classnames'
import React, { ForwardRefRenderFunction, useState } from 'react'

import { AccordionIdNames } from './accordion.component'
import styles from './accordion.module.css'
import AccordionButton from './button.component'
import AccordionContent from './content.component'

const renderContent = (text: string): React.ReactElement => (
  <Text fontSize="xs" fontWeight="medium">
    {text}
  </Text>
)

export interface AccordionItemProps {
  title: string | React.ReactNode | React.ReactNode[]
  children: string | (() => React.ReactNode)
  iconType?: IconNames
  iconGradient?: keyof typeof GradientNames
}

interface AccordionItemComponentProps
  extends AccordionItemProps,
    AccordionIdNames {
  id: number
  setCurrentFocusAccordion: (index?: number) => void
  totalItems: number
  onToggleExpanded: (isOpen: boolean) => void
  theme: 'standard' | 'alternative'
}

export const accordionClassName = 'accordionItemContainer'
// Accordion resource - https://www.w3.org/TR/wai-aria-practices-1.1/examples/accordion/accordion.html
const AccordionItem: ForwardRefRenderFunction<
  HTMLButtonElement,
  AccordionItemComponentProps
> = (
  {
    title,
    children,
    id,
    totalItems,
    setCurrentFocusAccordion,
    sectionId,
    buttonId,
    onToggleExpanded,
    iconType,
    iconGradient,
    theme,
  }: AccordionItemComponentProps,
  ref,
) => {
  const [isExpanded, setExpanded] = useState<boolean>(false)

  const content =
    typeof children === 'string' ? () => renderContent(children) : children

  const { trackEvent } = useAnalytics()

  return (
    <div
      className={classnames(
        styles[accordionClassName],
        styles[`${accordionClassName}__${theme}`],
      )}
    >
      <AccordionButton
        isExpanded={isExpanded}
        toggleExpanded={() => {
          const newState = !isExpanded
          onToggleExpanded(newState)
          setExpanded(newState)
          trackEvent({
            eventName: `FAQ click`,
            data: {
              event: {
                name: 'click',
                category: 'FAQs',
                action: title,
                label: newState ? 'expand' : 'collapse',
              },
            },
            type: TrackingEventTypes.ADOBE,
          })
        }}
        setFocusActive={setCurrentFocusAccordion}
        id={id}
        ref={ref}
        buttonText={title}
        totalItems={totalItems}
        buttonId={buttonId}
        sectionId={sectionId}
        iconType={iconType}
        iconGradient={iconGradient}
      />
      <AccordionContent
        isExpanded={isExpanded}
        id={id}
        buttonId={buttonId}
        sectionId={sectionId}
        content={content}
      />
    </div>
  )
}

export default React.forwardRef(AccordionItem)
